<template>
  <RevDrawer
    :name="MODAL_NAME"
    :title="i18n(translations.additionalCountriesModalTitle)"
  >
    <template #body="{ close }">
      <RevLoadingScreen
        v-if="isSubmitting"
        class="mt-10 self-center"
        :text="i18n(translations.updatingAdditionalCountries)"
        variant="light"
      />
      <template v-if="!isSubmitting">
        <RevForm
          :id="FORM_ID"
          :initial-values="initialFormValues"
          :validate
          @submit="handleSubmit"
        >
          <template #default="{ values }">
            <RevCheckbox
              v-for="country in formattedTaxResidenceCountryOptions"
              :id="country"
              :key="country"
              v-model="values.additionalCountries"
              class="my-8"
              :value="country"
            >
              <template #label>
                <div class="ml-2 flex items-center">
                  <Flag class="my-4 mr-8" :country-code="country" size="l" />
                  <div class="items-baseline">
                    <span class="mr-4">
                      {{ i18n.country(country) }}
                    </span>
                  </div>
                </div>
              </template>
            </RevCheckbox>

            <RevButton
              id="edit_main_tax_residence_country_button"
              class="mt-14"
              :form="FORM_ID"
              full-width="always"
              type="submit"
              variant="primary"
            >
              {{
                i18n(translations.submitButton, {
                  additionalCountryCount: values.additionalCountries?.length,
                })
              }}
            </RevButton>
            <RevLink
              id="cancel_form"
              class="m-auto mt-12 w-full"
              @click="close"
            >
              {{ i18n(translations.cancelButton) }}
            </RevLink>
          </template>
        </RevForm>
      </template>
    </template>
  </RevDrawer>
</template>

<script lang="ts" setup>
import { getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { type Country, sellerProfileAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevDrawer } from '@ds/components/Drawer'
import { FORM_VALID, RevForm } from '@ds/components/Form'
import { RevLink } from '@ds/components/Link'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal } from '@ds/components/ModalBase'

import type { Error } from '~/utils/apiError'

import { MODAL_NAMES } from '../../../../../../Profile.constants'

import translations from './AdditionalCountriesModal.translations'

const { openSuccessToast, openErrorToast } = useTheToast()

const MODAL_NAME = MODAL_NAMES.EDIT_TAX_ADDITIONAL_RESIDENCE_COUNTRIES

const i18n = useI18n()
const FORM_ID = 'edit_secondary_tax_residence_countries_form'
const emit = defineEmits(['additional-countries-updated'])

type AdditionalTaxResidenceCountriesFormValue = {
  additionalCountries: Country[]
}

const props = withDefaults(
  defineProps<{
    mainCountry?: Country | null
    additionalCountries?: Country[]
    taxResidenceCountryOptions?: Country[]
  }>(),
  {
    mainCountry: null,
    additionalCountries: () => [],
    taxResidenceCountryOptions: () => [],
  },
)
const isSubmitting = ref(false)

const initialFormValues = computed<AdditionalTaxResidenceCountriesFormValue>(
  () => ({
    additionalCountries: props.additionalCountries,
  }),
)

const formattedTaxResidenceCountryOptions = computed(() => {
  return props.taxResidenceCountryOptions.filter(
    (countryCode) => countryCode !== props.mainCountry,
  )
})

function validate() {
  return FORM_VALID
}

async function handleSubmit(values: AdditionalTaxResidenceCountriesFormValue) {
  isSubmitting.value = true
  try {
    await $httpFetch(sellerProfileAPI.updateTaxResidenceCountries, {
      body: {
        mainResidenceCountry: props.mainCountry,
        otherResidenceCountries: values.additionalCountries,
      },
    })

    openSuccessToast({
      title: i18n(translations.additionalCountriesUpdateSuccessTitle),
      content: i18n(translations.additionalCountrieUpdateSuccessContent),
    })

    emit('additional-countries-updated')

    closeModal(MODAL_NAME)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as Error),
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>
